import { memo, useMemo } from 'react';
import { LABEL } from '@veriables';
import { ReactComponent as DeleteIcon } from '@assets/images/delete.svg';
import { ReactComponent as VerticalEllipsis } from '@assets/images/verticalEllipsis.svg';
import { TouchableButton } from '@components/common/Touchable';
import styles from './cart.module.scss';
import cn from 'classnames';
const getBtnBg = serverAppTheme => serverAppTheme?.primaryBtn ?? '#0060FF';
const getSendBtnBg = serverAppTheme =>
  serverAppTheme?.secondaryBtn ?? '#4B0082';

const EmptyRow = ({
  pendingItems,
  onAction,
  serverAppTheme,
  isSendOrderMode,
  showOrderSendMode,
}) => {

  const totalCount = useMemo(
    () =>
      (pendingItems ?? []).reduce(
        (sum, item) => sum + (Number(item?.quantity) || 0),
        0
      ),
    [pendingItems]
  );
  const unheldCount = useMemo(
    () =>
      (pendingItems ?? []).reduce(
        (sum, item) => (!item.sendDate ? sum + (Number(item?.quantity) || 0) : sum),
        0
      ),
    [pendingItems]
  );
  const hasPending = totalCount > 0;
  const hasUnheld = unheldCount > 0;
  const fontColor = serverAppTheme?.btn_font_color ?? '#fff';
  const disabledColor = serverAppTheme?.btn_font_color_disabled ?? '#aaa';
  const sendBg = getSendBtnBg(serverAppTheme);
  const sendActiveBg = serverAppTheme?.sendOrderModeBg || '#d2d2d2';
  const activeSendStyle = isSendOrderMode
    ? { background: sendActiveBg, color: hasUnheld ? fontColor : disabledColor }
    : { background: hasUnheld ? sendBg : (serverAppTheme?.primary_btn_disable ?? sendBg), color: hasUnheld ? fontColor : disabledColor };

  return (
    <div className={styles.emptyRow}>
      <div className={styles.emptyDeleteAll}>
        <TouchableButton
          type="button"
          className={styles.actionBtn}
          style={{
            background: hasPending ? getBtnBg(serverAppTheme) : (serverAppTheme?.primary_btn_disable ?? getBtnBg(serverAppTheme)),
            color: hasPending ? fontColor : disabledColor,
          }}
          onClick={() => hasPending && onAction('deleteAll')}
          disabled={!hasPending}
        >
          <DeleteIcon
            fill={hasPending ? fontColor : disabledColor}
            width={20}
            height={20}
            style={{ marginRight: '4px' }}
          />
          ({totalCount})
        </TouchableButton>
      </div>
      <div className={styles.emptySendGroup}>
        <TouchableButton
          type="button"
          className={styles.sendAllBtn}
          style={{
            ...activeSendStyle,
            borderTopRightRadius: showOrderSendMode ? 0 : '4px',
            borderBottomRightRadius: showOrderSendMode ? 0 : '4px',
          }}
          onClick={() => hasUnheld && onAction('sendAll')}
          disabled={!hasUnheld}
        >
          <span
            className="position-relative"
            style={{ top: '-1px', marginRight: '2px' }}
          >
            {LABEL().SEND_ALL}
          </span>
          ({unheldCount})
        </TouchableButton>
        {showOrderSendMode && (
          <TouchableButton
            type="button"
            className={styles.orderSendModeBtn}
            style={activeSendStyle}
            onClick={() => hasUnheld && onAction('orderSendMode')}
            disabled={!hasUnheld}
          >
            <VerticalEllipsis
              fill={hasUnheld ? (fontColor) : disabledColor}
              width={25}
              height={25}
            />
          </TouchableButton>
        )}
      </div>
    </div>
  );
};

const SelectedGrid = ({ actions, count, onAction, serverAppTheme, showOrderSendMode, isSendOrderMode }) => {
  const rows = [];
  const fontColor = serverAppTheme?.btn_font_color ?? '#fff';
  const sendBg = getSendBtnBg(serverAppTheme);
  const sendActiveBg = serverAppTheme?.sendOrderModeBg || '#d2d2d2';
  const sendActiveColor = serverAppTheme?.primary_text || '#000';

  if (actions.length === 1) {
    const singleAction = actions[0];
    const isDelete = singleAction.key === 'deleteSelected';
    return (
      <TouchableButton
        type="button"
        className={cn(styles.actionBtn, 'col-6')}
        style={{ background: getBtnBg(serverAppTheme), color: fontColor }}
        onClick={(e) => { onAction(singleAction.key); e.preventDefault()}}
      >
        <span className="position-relative" style={{ top: '-1px' }}>
          {isDelete ? `${singleAction.name} (${count})` : singleAction.name}
        </span>
      </TouchableButton>
    );
  }

  const fullWidthSend = actions.length >= 4 ? actions.find(a => a.key === 'sendSelected') : null;
  const mainActions = fullWidthSend ? actions.filter(a => a.key !== 'sendSelected') : actions;
  const sendInLastRow = fullWidthSend && mainActions.length % 2 === 1;

  for (let i = 0; i < mainActions.length; i += 2) {
    const left = mainActions[i];
    const isLastIter = i + 1 >= mainActions.length;
    const right = isLastIter && sendInLastRow ? fullWidthSend : mainActions[i + 1];
    const isRightSend = right?.key === 'sendSelected';

    rows.push(
      <div key={left.key} className={cn(styles.gridRow,'mt-2')}>
        <div className={styles.gridCellLeft}>
          <TouchableButton
            type="button"
            className={styles.actionBtn}
            style={{
              background: getBtnBg(serverAppTheme),
              color: fontColor,
            }}
            onClick={(e) => {onAction(left.key); e.preventDefault()}}
          >
            <span className="position-relative" style={{ top: '-1px' }}>
              {left.key === 'deleteSelected'
                ? `${left.name} (${count})`
                : left.name}
            </span>
          </TouchableButton>
        </div>
        {right && (
          <div className={styles.gridCellRight}>
            {isRightSend ? (
              <div className={styles.emptySendGroup}>
                <TouchableButton
                  type="button"
                  className={styles.sendAllBtn}
                  style={{
                    background: isSendOrderMode ? sendActiveBg : sendBg,
                    color: isSendOrderMode ? sendActiveColor : fontColor,
                    borderTopRightRadius: showOrderSendMode ? 0 : '4px',
                    borderBottomRightRadius: showOrderSendMode ? 0 : '4px',
                  }}
                  onClick={(e) => {onAction(right.key); e.preventDefault()}}
                >
                  <span className="position-relative" style={{ top: '-1px', marginRight: '2px' }}>
                    {`${right.name} (${count})`}
                  </span>
                </TouchableButton>
                {showOrderSendMode && (
                  <TouchableButton
                    type="button"
                    className={styles.orderSendModeBtn}
                    style={{
                      background: isSendOrderMode ? sendActiveBg : sendBg,
                      color: isSendOrderMode ? sendActiveColor : fontColor,
                      width: 20,
                      flex: '0 0 32px',
                    }}
                    onClick={() => onAction('orderSendMode')}
                  >
                    <VerticalEllipsis
                      fill={isSendOrderMode ? sendActiveColor : fontColor}
                      width={20}
                      height={20}
                    />
                  </TouchableButton>
                )}
              </div>
            ) : (
              <TouchableButton
                type="button"
                className={styles.actionBtn}
                style={{
                  background: getBtnBg(serverAppTheme),
                  color: fontColor,
                }}
                onClick={() => onAction(right.key)}
              >
                <span className="position-relative" style={{ top: '-1px', marginRight: '2px' }}>
                  {right.key === 'hold'
                    ? `${right.name} (${count})`
                    : right.name}
                </span>
              </TouchableButton>
            )}
          </div>
        )}
      </div>
    );
  }
  if (fullWidthSend && !sendInLastRow) {
    rows.push(
      <div key="send-full-row" className={styles.gridRow}>
        <div className={cn(styles.emptySendGroup,'mt-2')}>
          <TouchableButton
            type="button"
            className={styles.sendAllBtn}
            style={{
              background: isSendOrderMode ? sendActiveBg : sendBg,
              color: isSendOrderMode ? sendActiveColor : fontColor,
              borderTopRightRadius: showOrderSendMode ? 0 : '4px',
              borderBottomRightRadius: showOrderSendMode ? 0 : '4px',
            }}
            onClick={() => onAction(fullWidthSend.key)}
          >
            <span className="position-relative" style={{ top: '-1px', marginRight: '2px' }}>
              {`${fullWidthSend.name} (${count})`}
            </span>
          </TouchableButton>
          {showOrderSendMode && (
            <TouchableButton
              type="button"
              className={styles.orderSendModeBtn}
              style={{
                background: isSendOrderMode ? sendActiveBg : sendBg,
                color: isSendOrderMode ? sendActiveColor : fontColor,
                width: 20,
                flex: '0 0 32px',
              }}
              onClick={() => onAction('orderSendMode')}
            >
              <VerticalEllipsis
                fill={isSendOrderMode ? sendActiveColor : fontColor}
                width={20}
                height={20}
              />
            </TouchableButton>
          )}
        </div>
      </div>
    );
  }

  return rows;
};

const CartFooter = ({
  selectedActions,
  count,
  onAction,
  serverAppTheme,
  showOrderSendMode,
  pendingItems,
  isSendOrderMode,
}) => (
  <div className={styles.footerBar}>
    {selectedActions ? (
      <SelectedGrid
        actions={selectedActions}
        count={count}
        onAction={onAction}
        serverAppTheme={serverAppTheme}
        showOrderSendMode={showOrderSendMode}
        isSendOrderMode={isSendOrderMode}
      />
    ) : (
      <EmptyRow
        pendingItems={pendingItems}
        onAction={onAction}
        serverAppTheme={serverAppTheme}
        isSendOrderMode={isSendOrderMode}
        showOrderSendMode={showOrderSendMode}
      />
    )}
  </div>
);

export default memo(CartFooter);
